/* THE PHONE MENU'S BEHAVIOUR (2026-10-02) — markup in snippets/r-mnav.liquid, styles in r-custom.css "PHONE MENU".
   Hooks the theme's own <header-menu-drawer> (theme.js) by its events and buttons only; theme.js is never patched:
   · tabs: WAI-ARIA tabs (automatic activation, arrow keys); the choice is written to sessionStorage 'r-search-gender', the key the
     header search drawer's Women | Men switch already reads, so the two always agree;
   · panels: filled from /collections/{womens|mens}?view=rmnav (categories) and ?view=rnav (designers, the same JSON and
     sessionStorage key the desktop designer column uses), cached for the session, prefetched when the menu button is pressed;
   · where you are: aria-current="page" on the exact page, "true" on the row you are inside (Baymard: 94% of mobile sites don't
     show this; shoppers arriving on a product page open the menu to find out where they are);
   · focus: a panel's heading on open, the row that opened it on Back (the theme drops focus to <body> both ways);
   · Klaviyo's teaser is hidden while the menu is open (html.r-mnav-open), so nothing covers the menu's bottom-left corner.
   · RACE GUARDS (v2.1, 2026-10-02, after an independent test pass that reproduced every one of these with real taps):
     taps on a panel's controls and links are swallowed while any panel is moving (ours or the theme's), so two quick taps can
     never leave two panels on screen; every open starts clean even when the theme skipped its own close clean-up (reopening
     within ~0.4s cancels the close, so 'dialog:after-hide' never fires); focus moves at once, never on a timer.
     ⚠️ Never .cancel() a theme animation: Motion (vendor.min.js) resolves its promise only on finish, and theme.js waits on that
        promise before it accepts another panel switch, so a cancelled one leaves every category row dead until reload. .finish().
        Motion ALSO cancels by itself when a second animation starts on the same element and property: with reduced motion the
        theme's open fades main in while a close-interrupted switch is still fading it out. So every panel animation is finished
        the moment the menu starts to close, and again (capture phase, ahead of the theme) the moment it starts to open (v2.3);
        panel taps are ignored while the drawer is closing, and an open that found a switch still running is cleaned up (v2.4).
   Does nothing at 1000px and wider, where the theme never opens this drawer, and closes the drawer if a tablet rotates past it. */
(() => {
  const drawer = document.getElementById('menu-drawer');
  if (!drawer || !drawer.classList.contains('r-mnav') || drawer.dataset.rMnavReady) return;
  drawer.dataset.rMnavReady = '1';

  const phone = window.matchMedia('(max-width: 999px)');
  const html = document.documentElement;
  const DEPT_KEY = 'r-search-gender';
  const URLS = { women: '/collections/womens', men: '/collections/mens' };
  const ROW_MIN = parseInt(drawer.dataset.rowMin, 10) || 3;
  /* the fewest products for a row to open a panel instead of linking straight to its page (level 1 in the snippet, level 2 here) */
  const PANEL_MIN = parseInt(drawer.dataset.panelMin, 10) || 20;
  const POP_MIN = parseInt(drawer.dataset.popularMin, 10) || 5;
  const SUB_ORDER = drawer.dataset.subOrder || 'count';
  const here = location.pathname.replace(/\/$/, '') || '/';
  const fold = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const path = (u) => { try { return new URL(u, location.origin).pathname.replace(/\/$/, ''); } catch (e) { return ''; } };

  const store = {
    get(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { /* private mode */ } },
  };

  /* ---------- data ---------- */
  const inflight = {};
  const getJSON = (url, key, pick) => {
    const cached = store.get(key);
    if (cached) {
      try { return Promise.resolve(JSON.parse(cached)); } catch (e) { /* refetch */ }
    }
    if (!inflight[url]) {
      inflight[url] = fetch(url, { credentials: 'same-origin' })
        .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then((d) => { const v = pick ? pick(d) : d; store.set(key, JSON.stringify(v)); return v; })
        .catch((e) => { delete inflight[url]; throw e; });
    }
    return inflight[url];
  };
  /* rmnav: our own versioned key. rnav: the desktop designer column's key and value shape (the bare designers array). */
  const categories = (dept) => getJSON(URLS[dept] + '?view=rmnav', 'r-mnav:v2:' + URLS[dept], null); /* v2: level 3 */
  const designers = (dept) => getJSON(URLS[dept] + '?view=rnav', 'r-designers:' + URLS[dept] + '?view=rnav', (d) => (d && d.designers) || []);
  const prefetch = (dept) => {
    if (!phone.matches || !URLS[dept]) return;
    categories(dept).catch(() => {});
    designers(dept).catch(() => {});
  };

  /* ---------- tabs ---------- */
  const tabs = [...drawer.querySelectorAll('.r-mnav__tab')];
  const tablist = drawer.querySelector('.r-mnav__tabs');
  const selectTab = (dept, { focus = false, remember = false } = {}) => {
    /* v2.4.4: the underline slides on a tap or arrow key (remember is true only for those); the reset as the menu opens jumps */
    if (tablist) tablist.classList.toggle('r-mnav__tabs--slide', remember);
    tabs.forEach((t) => {
      const on = t.dataset.dept === dept;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      const list = document.getElementById(t.getAttribute('aria-controls'));
      if (list) list.hidden = !on;
      if (on && focus) t.focus();
    });
    if (remember) store.set(DEPT_KEY, dept);
  };
  tabs.forEach((t, i) => {
    t.addEventListener('click', () => { selectTab(t.dataset.dept, { remember: true }); prefetch(t.dataset.dept); });
    t.addEventListener('keydown', (e) => {
      let j = null;
      if (e.key === 'ArrowRight') j = (i + 1) % tabs.length;
      else if (e.key === 'ArrowLeft') j = (i - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') j = 0;
      else if (e.key === 'End') j = tabs.length - 1;
      if (j === null) return;
      e.preventDefault();
      selectTab(tabs[j].dataset.dept, { focus: true, remember: true });
      prefetch(tabs[j].dataset.dept);
    });
  });
  const pageDept = () => {
    const d = drawer.dataset.dept;
    if (d === 'women' || d === 'men') return d;
    const s = store.get(DEPT_KEY);
    return s === 'men' || s === 'women' ? s : 'women';
  };

  /* ---------- where you are ---------- */
  /* A category's sale page (womens-clothing-on-sale) is its panel's Sale row, so a category that opens a panel is marked on it;
     a category that is a plain link has no Sale row, so there the department's Sale row is marked instead. */
  const onSale = /-on-sale$/.test(here);
  const under = (p) => !!p && (here === p || (!onSale && here.startsWith(p + '-')));
  const within = (p) => !!p && (here === p || here.startsWith(p + '-'));
  /* A product page's full category paths (every visible '>' tag, header.liquid), so levels 2 and 3 can be marked too, by title. */
  const key = (s) => fold(s).replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '');
  const catPaths = (drawer.dataset.catPaths || '').split('|').map((t) => t.split('>').map(key)).filter((t) => t.length > 1);
  const inPath = (dept, ...titles) => dept === pageDept() && catPaths.some((t) => titles.every((x, i) => t[i] === key(x)));
  const markCurrent = () => {
    const cat = fold(drawer.dataset.cat);
    const deptList = 'r-mnav-list-' + pageDept();
    drawer.querySelectorAll('.r-mnav__list .r-mnav__row').forEach((row) => {
      row.removeAttribute('aria-current');
      const href = row.getAttribute('href');
      const catUrl = row.dataset.catUrl ? path(row.dataset.catUrl) : '';
      const mine = (row.closest('[role="tabpanel"]') || {}).id === deptList;
      if (href && path(href) === here) row.setAttribute('aria-current', 'page');
      else if (href ? under(catUrl) : within(catUrl)) row.setAttribute('aria-current', 'true');
      /* a product page: its category in its own department only */
      else if (cat && mine && row.dataset.catTitle && fold(row.dataset.catTitle) === cat) row.setAttribute('aria-current', 'true');
    });
    /* any other sale page (a link-only category's, a designer's): the department's Sale row, if nothing else is marked */
    const list = document.getElementById(deptList);
    if (onSale && list && !list.querySelector('.r-mnav__row[aria-current]')) {
      const saleRow = list.querySelector('.r-mnav__row--sale');
      if (saleRow) saleRow.setAttribute('aria-current', 'true');
    }
    /* each category panel's own 'All Women's Clothing' row is that category's page */
    drawer.querySelectorAll('header-menu-drawer-panel[data-r-fill="cat"] .r-mnav__all').forEach((a) => {
      if (path(a.getAttribute('href')) === here) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current');
    });
  };

  /* ---------- panels ---------- */
  const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };
  const link = (title, url, inside) => {
    const li = el('li');
    const a = el('a', null, title);
    a.href = url;
    if (path(url) === here) a.setAttribute('aria-current', 'page');
    else if (inside) a.setAttribute('aria-current', 'true');
    li.appendChild(a);
    return li;
  };
  /* Placeholder bars only if the data is slow (300ms); given up after 4s so the panel keeps its working "All …" row. */
  const waiting = (panel, promise) => {
    const rows = panel.querySelector('[data-r-rows]');
    const t1 = setTimeout(() => {
      if (rows.childElementCount) return;
      for (let i = 0; i < 3; i++) rows.appendChild(el('li', 'r-mnav__skel'));
    }, 300);
    const t2 = setTimeout(() => rows.querySelectorAll('.r-mnav__skel').forEach((s) => s.remove()), 4000);
    return promise.finally(() => { clearTimeout(t1); clearTimeout(t2); rows.querySelectorAll('.r-mnav__skel').forEach((s) => s.remove()); });
  };

  const order = (list) => (SUB_ORDER === 'count'
    ? list.map((s, i) => [s, i]).sort((a, b) => b[0].count - a[0].count || a[1] - b[1]).map((x) => x[0])
    : list);

  /* A level-2 row that opens level 3: one full-width button (one tap target, Baymard), chevron, "you are inside" (never "page": it
     opens a panel, it doesn't navigate; level 3's 'All …' row is the page) when the page is it or one of its children. Its data
     rides on the element so the shared level-3 panel can be filled from it. */
  const l3Opener = (sub, kids, inside, catTitle) => {
    const li = el('li');
    const b = el('button', 'r-mnav__subbtn');
    b.type = 'button';
    b.setAttribute('aria-expanded', 'false');
    b.setAttribute('aria-controls', 'r-mnav-l3');
    b.dataset.rL3Open = '';
    b.appendChild(el('span', null, sub.title));
    b.insertAdjacentHTML('beforeend', '<svg class="r-mnav__chev" aria-hidden="true" focusable="false" width="7" height="12" viewBox="0 0 7 12"><path d="M1 1l5 5-5 5" fill="none" stroke="currentColor" stroke-width="1.25"/></svg>');
    if (inside || under(path(sub.url))) b.setAttribute('aria-current', 'true');
    b.rMnavSub = { sub, kids, catTitle };
    li.appendChild(b);
    return li;
  };

  const fillCategory = (panel) => {
    if (panel.dataset.rFilled) return;
    const dept = panel.dataset.dept;
    waiting(panel, categories(dept)).then((data) => {
      const want = path(panel.dataset.catUrl);
      const cat = ((data && data.categories) || []).find((c) => path(c.url) === want);
      if (!cat) return;
      const subs = order((cat.subs || []).filter((s) => s.count >= ROW_MIN));
      const frag = document.createDocumentFragment();
      /* Sale first, named and coloured as on desktop (Rishi 2026-09-28: "each link is named 'sale'"; 2026-10-02 on the phone menu:
         the Sale button "is missing") */
      if (cat.sale && cat.sale.url && cat.sale.count >= ROW_MIN) {
        const li = link('Sale', cat.sale.url);
        li.classList.add('r-mnav__sale');
        frag.appendChild(li);
      }
      subs.forEach((s) => {
        const kids = order((s.subs || []).filter((k) => k.count >= ROW_MIN));
        const inside = inPath(dept, cat.title, s.title);
        /* a third level only for a sub-category with PANEL_MIN products or more, as at level 1 (Rishi: "20 items not 60") */
        const deep = kids.length >= 2 && s.count >= PANEL_MIN;
        frag.appendChild(deep ? l3Opener(s, kids, inside, cat.title) : link(s.title, s.url, inside || under(path(s.url))));
      });
      panel.querySelector('[data-r-rows]').replaceChildren(frag);
      panel.dataset.rFilled = '1';
    }).catch(() => {});
  };

  const fillDesigners = (panel) => {
    if (panel.dataset.rFilled) return;
    const dept = panel.dataset.dept;
    const vendor = fold(drawer.dataset.vendor);
    waiting(panel, designers(dept)).then((list) => {
      if (!Array.isArray(list) || !list.length) throw new Error('empty');
      const byPath = new Map(list.map((d) => [path(d.url), d]));
      /* POPULAR: the theme setting's order, each only while it has at least POP_MIN live pieces here */
      const popular = (panel.dataset.popular || '').split(',').map((h) => byPath.get('/collections/' + h.trim())).filter((d) => d && d.count >= POP_MIN);
      const popUl = panel.querySelector('[data-r-popular]');
      popular.forEach((d) => popUl.appendChild(link(d.name, d.url)));
      panel.querySelector('[data-r-popular-label]').hidden = !popular.length;
      /* A–Z: every designer with stock in this department; the first of each letter carries the letter in the gutter */
      const az = [...list].filter((d) => d && d.name && d.url).sort((a, b) => fold(a.name).localeCompare(fold(b.name)));
      const frag = document.createDocumentFragment();
      let last = '';
      az.forEach((d) => {
        const li = link(d.name, d.url);
        li.dataset.name = d.name;
        const first = fold(d.name).charAt(0).toUpperCase();
        const letter = /[A-Z]/.test(first) ? first : '#';
        if (letter !== last) { li.dataset.letter = letter; last = letter; }
        if (vendor && fold(d.name) === vendor) {
          li.classList.add('r-mnav__here');
          const a = li.querySelector('a');
          if (!a.hasAttribute('aria-current')) a.setAttribute('aria-current', 'true'); /* not by weight alone */
        }
        frag.appendChild(li);
      });
      panel.querySelector('[data-r-rows]').replaceChildren(frag);
      panel.querySelector('[data-r-az-label]').hidden = false;
      panel.dataset.rFilled = '1';
      wireFilter(panel, dept);
    }).catch(() => {
      /* No designer data: the department's designers page instead, and no search box that couldn't search (copy check 2026-10-02) */
      panel.querySelector('[data-r-fallback]').hidden = false;
      const search = panel.querySelector('.r-mnav__search');
      if (search) search.hidden = true;
    });
  };

  /* The designer filter: desktop's matcher (word-start, accents and punctuation ignored) plus the search router's own alias
     table for 'ysl' → Saint Laurent and typo fixes (window.rSearchRoute, loaded deferred on every page). A miss hands the
     words to search, filtered to this department, where the router lands known designers and categories on their page. */
  const wireFilter = (panel, dept) => {
    const input = panel.querySelector('.r-mnav__input');
    const clear = panel.querySelector('.r-mnav__clear');
    const rows = [...panel.querySelectorAll('[data-r-rows] > li')];
    const words = rows.map((li) => fold(li.dataset.name).split(/[^a-z0-9]+/).filter(Boolean));
    const empty = panel.querySelector('[data-r-empty]');
    const handoff = panel.querySelector('[data-r-handoff]');
    const live = panel.querySelector('[data-r-live]');
    const popUl = panel.querySelector('[data-r-popular]');
    const popLabel = panel.querySelector('[data-r-popular-label]');
    const azLabel = panel.querySelector('[data-r-az-label]');
    let speak = null;
    const run = () => {
      const raw = input.value.trim();
      const q = fold(raw).replace(/[^a-z0-9]+/g, '');
      const typing = q !== '';
      clear.hidden = !typing;
      panel.classList.toggle('r-mnav--typing', typing);
      /* while typing, matches sit straight under the field (above the keyboard): Popular and the headings step aside */
      popUl.hidden = typing;
      popLabel.hidden = typing || !popUl.childElementCount;
      azLabel.hidden = typing;
      let alias = '';
      const R = window.rSearchRoute;
      if (typing && R && R.parse) {
        try { const p = R.parse(raw); if (p && p.d && !p.c) alias = fold(p.d).replace(/[^a-z0-9]+/g, ''); } catch (e) { /* plain match */ }
      }
      let shown = 0;
      rows.forEach((li, i) => {
        const hit = !typing || words[i].some((_, j) => words[i].slice(j).join('').startsWith(q)) || (alias && words[i].join('') === alias);
        li.hidden = !hit;
        if (hit) shown += 1;
      });
      let notCarried = false;
      if (typing && !shown && R && R.notCarried) { try { notCarried = R.notCarried(raw); } catch (e) { /* no */ } }
      empty.hidden = !typing || shown > 0;
      empty.textContent = notCarried ? 'We are sorry, we do not carry this designer yet.' : 'No designers found';
      /* A designer we don't carry gets the apology and nothing else: search would only repeat it (copy check 2026-10-02). */
      handoff.hidden = !typing || shown > 0 || notCarried;
      panel.dataset.rNotCarried = notCarried ? '1' : '';
      if (typing) {
        const params = new URLSearchParams({ q: raw, type: 'product', 'filter.p.product_type': dept === 'men' ? 'Mens' : 'Womens' });
        handoff.href = (window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/') + 'search?' + params.toString();
        handoff.textContent = 'Search "' + raw + '"';
      }
      clearTimeout(speak);
      speak = setTimeout(() => { live.textContent = !typing ? '' : shown === 0 ? empty.textContent : shown === 1 ? '1 designer' : shown + ' designers'; }, 400);
      return shown;
    };
    input.addEventListener('input', run);
    input.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      const visible = rows.filter((li) => !li.hidden);
      if (input.value.trim() && visible.length === 1) location.href = visible[0].querySelector('a').href;
      else if (input.value.trim() && !panel.dataset.rNotCarried) location.href = handoff.href;
    });
    clear.addEventListener('click', () => { input.value = ''; run(); input.focus(); });
    panel.rMnavReset = () => { if (input.value) { input.value = ''; run(); } };
  };

  /* ---------- level 3 (2026-10-02, Rishi: "I need a way for the nav to go another level deep") ----------
     The theme's own panel slide only reads right one level deep: a non-main panel always leaves to the RIGHT, so going from level 2
     to level 3 through it would look like going back. Level 3 therefore slides itself, mirroring the theme's two moves exactly:
     deeper = the new panel enters from the right (0.4s) while the one behind shifts 30% left; back = it leaves to the right (0.25s)
     while the one behind returns. Reduced motion: a 0.2s fade, as the theme does. */
  const l3 = document.getElementById('r-mnav-l3');
  const main = document.getElementById('menu-drawer-panel-main');
  const panels = () => [...drawer.querySelectorAll('header-menu-drawer-panel')];
  const shown = () => panels().filter((p) => p.classList.contains('is-visible'));
  let l3From = null; /* the level-2 panel and the row that opened level 3 */
  let l3Row = null;
  let sliding = false;
  let themeUntil = 0; /* a frame or two after a theme toggle, until its switch has both panels on screen */
  let settling = 0; /* clean-ups still to run after a reopen that needed putting right (a count: two can overlap, v2.4.1) */
  let openGen = 0; /* bumped on every open, so a clean-up timer left from an earlier open can't release this one's hold */
  let closing = false; /* from 'dialog:before-hide' until the drawer is hidden or reopened (v2.4) */
  let wasMoving = false; /* a panel animation was still running when the menu reopened (v2.4) */
  let lastTap = null; /* the last accepted panel tap {x, y, t}, for the double-tap absorber (v2.4) */

  /* Is anything moving? Our slide; the theme's switch, which keeps BOTH panels on screen from the tap until a frame after its
     animations end (so two visible panels is the signal, not a fixed 0.45s that held taps ~0.2s after the move had ended); or a
     reopen still settling. The menu's own opening fade (one panel, reduced motion) is not a move: taps go straight through. */
  const moving = () => sliding || settling > 0 || closing || performance.now() < themeUntil || shown().length > 1;

  const EASE_IN = 'cubic-bezier(0.645, 0.045, 0.355, 1)';
  const slide = (from, to, deeper) => {
    sliding = true;
    to.inert = false;
    from.inert = true; /* the panel leaving can't take focus or taps while it slides out */
    to.classList.add('is-visible');
    /* level 3 is shared, so it would open at the last list's scroll (setting it while hidden does nothing) */
    if (deeper) { const b = to.querySelector('.r-mnav__body'); if (b) b.scrollTop = 0; }
    const still = !window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
    let runs;
    if (still) {
      runs = [to.animate({ opacity: [0, 1] }, { duration: 200 }), from.animate({ opacity: [1, 0] }, { duration: 200 })];
    } else if (deeper) {
      runs = [to.animate({ transform: ['translateX(100%)', 'translateX(0)'] }, { duration: 400, easing: EASE_IN }),
              from.animate({ transform: ['translateX(0)', 'translateX(-30%)'] }, { duration: 400, easing: EASE_IN })];
    } else {
      runs = [from.animate({ transform: ['translateX(0)', 'translateX(100%)'] }, { duration: 250, easing: 'ease-in-out' }),
              to.animate({ transform: ['translateX(-30%)', 'translateX(0)'] }, { duration: 250, easing: 'ease-in-out' })];
    }
    return Promise.all(runs.map((r) => r.finished.catch(() => {}))).then(() => {
      from.classList.remove('is-visible');
      sliding = false;
    });
  };
  const openL3 = (btn) => {
    const data = btn.rMnavSub;
    const from = btn.closest('header-menu-drawer-panel');
    const on = shown();
    /* only from a level-2 panel that is alone on screen and still */
    if (!l3 || !data || !from || moving() || on.length !== 1 || on[0] !== from) return;
    const dept = from.dataset.dept;
    const poss = dept === 'men' ? "Men's" : "Women's";
    const parentTitle = (from.querySelector('.r-mnav__title') || {}).textContent || '';
    l3.querySelector('[data-r-l3-parent]').textContent = parentTitle;
    l3.querySelector('[data-r-l3-back]').setAttribute('aria-label', 'Back to ' + parentTitle);
    l3.querySelector('.r-mnav__title').textContent = data.sub.title;
    const all = l3.querySelector('[data-r-l3-all]');
    all.href = data.sub.url;
    all.textContent = 'All ' + poss + ' ' + data.sub.title;
    if (path(data.sub.url) === here) all.setAttribute('aria-current', 'page'); else all.removeAttribute('aria-current');
    const frag = document.createDocumentFragment();
    data.kids.forEach((k) => frag.appendChild(link(k.title, k.url, inPath(dept, data.catTitle || parentTitle, data.sub.title, k.title))));
    l3.querySelector('[data-r-rows]').replaceChildren(frag);
    l3From = from;
    l3Row = btn;
    btn.setAttribute('aria-expanded', 'true');
    slide(from, l3, true);
    /* at once, not after the slide: a delayed focus could land in a menu the shopper has already closed */
    l3.querySelector('.r-mnav__title').focus({ preventScroll: true });
  };
  const closeL3 = () => {
    if (!l3From || moving()) return;
    const from = l3From;
    const row = l3Row;
    l3From = null;
    l3Row = null;
    slide(l3, from, false);
    if (row) { row.setAttribute('aria-expanded', 'false'); row.focus({ preventScroll: true }); }
  };

  /* FIRST, in the capture phase (ahead of the theme's own listener on this element): while anything is moving, a tap on a panel's
     controls or links does nothing, as the theme already does for its own switch. Without it, '← Women' tapped during level 3's
     slide (or Tops during the theme's) left two panels on screen, one painted over the other, with a Back that couldn't be hit; and
     with reduced motion a double tap on Tops followed the first link of a list the shopper never saw. */
  drawer.addEventListener('click', (e) => {
    if (!phone.matches) return;
    /* the department tabs too: mid-slide, a tap meant for '← Men' landed on the outgoing WOMEN tab and switched department */
    const hit = e.target.closest('[data-panel-toggle], [data-r-l3-open], [data-r-l3-back], header-menu-drawer-panel a, .r-mnav__tab');
    if (!hit) return;
    /* DOUBLE-TAP ABSORBER (v2.4): the second tap of a double tap, landing within 16px and 0.4s of an accepted panel tap, is
       swallowed even once the move has ended. Without it, with reduced motion a double tap on Clothing opened the level-3 row
       under the finger, and on men's pages a double tap on '← Men' hit the WOMEN tab that sits under it. Pointer taps only
       (e.detail > 0); a keyboard Enter is never absorbed. v2.4.1: 16px, under half the 36px row pitch (40px caught the row above
       or below, and Balmain under Designers), and Back after Back is never absorbed (two Backs are unambiguous). */
    const now = performance.now();
    const isBack = (n) => n.matches('[data-r-l3-back]') || (n.matches('[data-panel-toggle]') && n.getAttribute('aria-controls') === 'menu-drawer-panel-main');
    const twin = e.detail > 0 && lastTap && now - lastTap.t < 400 && Math.hypot(e.clientX - lastTap.x, e.clientY - lastTap.y) < 16
      && !(lastTap.back && isBack(hit));
    if (moving() || twin) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }
    const isPanelTap = hit.matches('[data-panel-toggle], [data-r-l3-open], [data-r-l3-back]');
    lastTap = isPanelTap && e.detail > 0 ? { x: e.clientX, y: e.clientY, t: now, back: isBack(hit) } : null;
    if (hit.hasAttribute('data-panel-toggle')) themeUntil = now + 80;
  }, true);

  drawer.addEventListener('click', (e) => {
    if (!phone.matches) return;
    const open = e.target.closest('[data-r-l3-open]');
    if (open) { e.preventDefault(); openL3(open); return; }
    if (e.target.closest('[data-r-l3-back]')) { e.preventDefault(); closeL3(); }
  });

  /* ---------- opening a panel, going back ---------- */
  let opener = null;
  drawer.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-panel-toggle]');
    if (!btn || !phone.matches) return;
    const target = document.getElementById(btn.getAttribute('aria-controls'));
    /* The theme's listener (on this element, registered first) has already run and shows the new panel synchronously; if it
       ignored the tap, nothing here changes either. */
    if (!target || !target.classList.contains('is-visible')) return;
    const leaving = btn.closest('header-menu-drawer-panel');
    target.inert = false;
    if (leaving && leaving !== target) leaving.inert = true; /* WebKit: a Tab mid-slide reached the outgoing panel, then <body> */
    if (target.hasAttribute('data-main-panel')) {
      /* Back: focus returns to the row that opened the panel, at once (a timer let focus fall to <body> meanwhile) */
      const back = opener;
      opener = null;
      drawer.querySelectorAll('.r-mnav__list [aria-expanded="true"]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      if (back) back.focus({ preventScroll: true });
      return;
    }
    opener = btn;
    btn.setAttribute('aria-expanded', 'true');
    const body = target.querySelector('.r-mnav__body');
    if (body) body.scrollTop = 0; /* a panel opens at its top (it kept its old scroll across a close and reopen) */
    if (target.dataset.rFill === 'cat') fillCategory(target);
    else if (target.dataset.rFill === 'designers') fillDesigners(target);
    const h = target.querySelector('.r-mnav__title');
    if (h) h.focus({ preventScroll: true });
  });

  /* ---------- open / close ---------- */
  /* Shopify's <shopify-account> names its button 'Account' while the pill reads 'Sign in' (WCAG 2.5.3: Voice Control users say
     what they see). Its shadow root is open; the name is put back on every open in case the component re-renders. */
  const nameSignIn = () => {
    const host = drawer.querySelector('shopify-account');
    const b = host && host.shadowRoot && host.shadowRoot.querySelector('[part~="signed-out-avatar"]');
    if (b && b.getAttribute('aria-label') !== 'Sign in') b.setAttribute('aria-label', 'Sign in');
  };
  if (window.customElements && drawer.querySelector('shopify-account')) {
    customElements.whenDefined('shopify-account').then(() => requestAnimationFrame(nameSignIn)).catch(() => {});
  }

  /* Every open starts on the main panel alone. Normally the theme's close clean-up has done this already; reopening within ~0.4s
     of closing cancels the close, so that clean-up never ran and the old panel was still on screen beside the main one. The
     theme's in-flight animations are FINISHED (see the note at the top), which runs its own clean-up a frame or two later; the
     state is then put right again once that has landed. */
  let settledAt = -1e9; /* when settle() last finished a running animation: the theme's clean-up for it lands 1-2 frames later */
  const settle = () => {
    let n = 0;
    panels().forEach((p) => p.getAnimations().forEach((a) => {
      if (a.playState === 'finished') return;
      n += 1;
      try { a.finish(); } catch (err) { /* done */ }
    }));
    if (n) settledAt = performance.now();
  };
  const clean = () => {
    panels().forEach((p) => {
      p.inert = false;
      if (p === main) return;
      p.classList.remove('is-visible');
      p.removeAttribute('style');
    });
    if (main && !main.classList.contains('is-visible')) { main.removeAttribute('style'); main.classList.add('is-visible'); }
    drawer.querySelectorAll('[aria-expanded="true"]').forEach((b) => { if (drawer.contains(b)) b.setAttribute('aria-expanded', 'false'); });
  };
  /* Closing: finish every panel animation at once (a switch still in flight, ours or the theme's), so the theme's promise
     settles and nothing is left for the next open to collide with. */
  drawer.addEventListener('dialog:before-hide', () => { if (phone.matches) { closing = true; settle(); } });
  /* Opening, in the CAPTURE phase on the document so it runs BEFORE the theme's own before-show (which starts main's opening
     fade, and with reduced motion that fade would cancel a fade-out still running on main and wedge the theme). */
  document.addEventListener('dialog:before-show', (e) => {
    if (e.target !== drawer || !phone.matches) return;
    /* a switch still running here (e.g. one tapped on the closing drawer) lands its own clean-up a frame later and can hide main,
       so this open counts as dirty and gets the late clean-up (v2.4: it showed a blank menu). So does a reopen while the close
       hasn't reported back yet (v2.4.1): WebKit can deliver that close's after-hide AFTER this open, and the theme then hides
       every panel. */
    openGen += 1;
    settling = 0; /* a new open voids every earlier clean-up timer (they check openGen), so its hold starts from zero */
    wasMoving = closing || performance.now() - settledAt < 150
      || panels().some((p) => p.getAnimations().some((a) => a.playState === 'running' || a.pending));
    closing = false;
    settle();
  }, true);
  drawer.addEventListener('dialog:before-show', () => {
    if (!phone.matches) return;
    const dirty = wasMoving || l3From || sliding || panels().some((p) => p !== main && p.classList.contains('is-visible'));
    wasMoving = false;
    if (dirty) {
      l3From = null;
      l3Row = null;
      sliding = false;
      themeUntil = 0;
      opener = null;
      clean();
      /* The theme's own clean-up for the animations just finished lands a frame or two later (frame.render) and may hide main
         again; put it right once that has run. Taps wait for it (settling), however slow the phone: before, a fixed 300ms gate
         let a category open first and the late clean-up then blanked it. */
      settling += 1;
      const gen = openGen;
      const done = () => { if (gen !== openGen || settling < 1) return; settling -= 1; clean(); };
      requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(() => {
        if (gen === openGen) clean();
        setTimeout(done, 120);
      })));
    }
    selectTab(pageDept());
    markCurrent();
    nameSignIn();
    html.classList.add('r-mnav-open');
    if (main) main.querySelectorAll('.r-mnav__body').forEach((b) => { b.scrollTop = 0; });
    drawer.querySelectorAll('[data-r-fill="designers"]').forEach((p) => p.rMnavReset && p.rMnavReset());
  });
  drawer.addEventListener('dialog:after-hide', () => {
    /* A STALE after-hide (v2.4.1): the close it belongs to was already reversed by a reopen (WebKit, reduced motion, a reopen
       right as the close ends). The theme's own after-hide has just hidden every panel of the OPEN menu, a frame from now; put
       main back once that lands, hold taps meanwhile, and skip every close reset (the menu is open). */
    if (drawer.open) {
      if (!phone.matches) return;
      /* v2.4.2: the theme's close sets display:none on the drawer itself just before this event; undo it the way show() sets it,
         inside this listener so no frame is ever painted with the open menu hidden. */
      drawer.style.setProperty('display', drawer.shadowRoot ? 'contents' : '');
      const btn = [...document.querySelectorAll('[aria-controls="menu-drawer"]')].find((b) => b.offsetParent);
      if (document.activeElement === document.body && btn) btn.focus({ preventScroll: true }); /* where a phone open leaves it */
      settling += 1;
      const gen = openGen;
      /* the theme's panel reset is queued (a promise, then Motion's frame.render) from ITS after-hide listener, which ran first; two
         microtasks later this rAF is registered after Motion's, so it runs right after the reset in the same frame: no empty flash */
      Promise.resolve().then(() => Promise.resolve()).then(() => requestAnimationFrame(() => { if (gen === openGen) clean(); }));
      requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(() => {
        if (gen !== openGen) return;
        clean();
        setTimeout(() => { if (gen !== openGen) return; clean(); settling = Math.max(0, settling - 1); }, 120);
      })));
      return;
    }
    if (phone.matches) settle(); /* a switch begun during the close completes while the drawer is shut */
    closing = false;
    lastTap = null;
    html.classList.remove('r-mnav-open');
    l3From = null;
    l3Row = null;
    sliding = false;
    settling = 0;
    panels().forEach((p) => { p.inert = false; });
    drawer.querySelectorAll('[aria-expanded="true"]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    opener = null;
  });
  /* A tablet rotated past 999px with the menu open: every phone style is gone and the theme never shows this drawer there. */
  const onPhoneChange = (e) => { if (!e.matches && drawer.open && drawer.hide) drawer.hide(); };
  if (phone.addEventListener) phone.addEventListener('change', onPhoneChange); else if (phone.addListener) phone.addListener(onPhoneChange);

  /* Prefetch: the moment the menu button is pressed, and once when the browser is idle (never on Save-Data). */
  document.addEventListener('pointerdown', (e) => {
    if (e.target.closest && e.target.closest('[aria-controls="menu-drawer"]')) prefetch(pageDept());
  }, { passive: true, capture: true });
  const idle = window.requestIdleCallback || ((f) => setTimeout(f, 2000));
  if (!(navigator.connection && navigator.connection.saveData)) idle(() => { if (phone.matches) categories(pageDept()).catch(() => {}); });
})();
